import { type CSSProperties, type ReactNode } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
export type CoachmarkSide = 'top' | 'right' | 'bottom' | 'left';
export type CoachmarkAlign = 'start' | 'center' | 'end';
export type CoachmarkBoxOffset = {
  topBottom: number;
  leftRight: number;
};
type CoachmarkTooltipProps = {
  open: boolean;
  side: CoachmarkSide;
  align: CoachmarkAlign;
  coachBoxOffset: CoachmarkBoxOffset;
  animateOnOpen?: boolean;
  children: ReactNode;
  content: ReactNode;
};
export const CoachmarkTooltip = ({
  open,
  side,
  align,
  coachBoxOffset,
  animateOnOpen = false,
  children,
  content
}: CoachmarkTooltipProps) => <TooltipPrimitive.Provider delayDuration={0} data-sentry-element="unknown" data-sentry-component="CoachmarkTooltip" data-sentry-source-file="CoachmarkTooltip.tsx">
    <TooltipPrimitive.Root open={open} data-sentry-element="unknown" data-sentry-source-file="CoachmarkTooltip.tsx">
      <TooltipPrimitive.Trigger asChild data-sentry-element="unknown" data-sentry-source-file="CoachmarkTooltip.tsx">{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Content side={side} align={align} sideOffset={side === 'top' || side === 'bottom' ? coachBoxOffset.topBottom : coachBoxOffset.leftRight} avoidCollisions={false} className={`z-modal overflow-visible bg-transparent p-0 shadow-none ${animateOnOpen ? 'duration-200 ease-out animate-in fade-in-0 motion-reduce:animate-none' : ''}`} data-sentry-element="unknown" data-sentry-source-file="CoachmarkTooltip.tsx">
        <div style={getBubbleSurfaceStyle(side, align)}>{content}</div>
        <FixedCoachmarkArrow side={side} align={align} data-sentry-element="FixedCoachmarkArrow" data-sentry-source-file="CoachmarkTooltip.tsx" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Root>
  </TooltipPrimitive.Provider>;
const FixedCoachmarkArrow = ({
  side,
  align
}: {
  side: CoachmarkSide;
  align: CoachmarkAlign;
}) => <span aria-hidden className="pointer-events-none absolute z-10 size-0" style={getArrowStyle(side, align)} data-sentry-component="FixedCoachmarkArrow" data-sentry-source-file="CoachmarkTooltip.tsx" />;
const ARROW_WIDTH = 13;
const ARROW_HEIGHT = 8;
const TOGGLE_WIDTH = 42;
const TOGGLE_HEIGHT = 24;
const ARROW_COLOR = 'white';
const VERTICAL_END_BUBBLE_OFFSET = 4;
const VERTICAL_END_BUBBLE_SURFACE_OFFSET = 10;
const isVerticalSide = (side: CoachmarkSide) => side === 'left' || side === 'right';
const getBubbleSurfaceStyle = (side: CoachmarkSide, align: CoachmarkAlign): CSSProperties => {
  if (isVerticalSide(side) && align === 'end') {
    return {
      transform: `translateY(${VERTICAL_END_BUBBLE_SURFACE_OFFSET}px)`
    };
  }
  return {};
};
const getArrowAlignedOffset = (side: CoachmarkSide, align: CoachmarkAlign) => {
  const anchorSize = isVerticalSide(side) ? TOGGLE_HEIGHT : TOGGLE_WIDTH;
  const alignedOffset = (anchorSize - ARROW_WIDTH) / 2;
  if (isVerticalSide(side) && align === 'end') {
    return alignedOffset + VERTICAL_END_BUBBLE_OFFSET;
  }
  return alignedOffset;
};
const getArrowStyle = (side: CoachmarkSide, align: CoachmarkAlign): CSSProperties => {
  const crossAxisStyle = getArrowCrossAxisStyle(side, align);
  if (side === 'bottom') {
    return {
      ...crossAxisStyle,
      top: -ARROW_HEIGHT + 1,
      borderLeft: `${ARROW_WIDTH / 2}px solid transparent`,
      borderRight: `${ARROW_WIDTH / 2}px solid transparent`,
      borderBottom: `${ARROW_HEIGHT}px solid ${ARROW_COLOR}`
    };
  }
  if (side === 'top') {
    return {
      ...crossAxisStyle,
      bottom: -ARROW_HEIGHT + 1,
      borderLeft: `${ARROW_WIDTH / 2}px solid transparent`,
      borderRight: `${ARROW_WIDTH / 2}px solid transparent`,
      borderTop: `${ARROW_HEIGHT}px solid ${ARROW_COLOR}`
    };
  }
  if (side === 'right') {
    return {
      ...crossAxisStyle,
      left: -ARROW_HEIGHT + 1,
      borderTop: `${ARROW_WIDTH / 2}px solid transparent`,
      borderBottom: `${ARROW_WIDTH / 2}px solid transparent`,
      borderRight: `${ARROW_HEIGHT}px solid ${ARROW_COLOR}`
    };
  }
  return {
    ...crossAxisStyle,
    right: -ARROW_HEIGHT + 1,
    borderTop: `${ARROW_WIDTH / 2}px solid transparent`,
    borderBottom: `${ARROW_WIDTH / 2}px solid transparent`,
    borderLeft: `${ARROW_HEIGHT}px solid ${ARROW_COLOR}`
  };
};
const getArrowCrossAxisStyle = (side: CoachmarkSide, align: CoachmarkAlign): CSSProperties => {
  const isVertical = isVerticalSide(side);
  if (align === 'center') {
    return isVertical ? {
      top: '50%',
      transform: 'translateY(-50%)'
    } : {
      left: '50%',
      transform: 'translateX(-50%)'
    };
  }
  if (align === 'start') {
    return isVertical ? {
      top: getArrowAlignedOffset(side, align)
    } : {
      left: getArrowAlignedOffset(side, align)
    };
  }
  return isVertical ? {
    bottom: getArrowAlignedOffset(side, align)
  } : {
    right: getArrowAlignedOffset(side, align)
  };
};